import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import './GameLibrary.css';

const GAMES = [
  {
    id: 'pool-of-radiance',
    title: 'Pool of Radiance',
    year: '1988',
    setting: 'Phlan, Forgotten Realms',
    tagline: 'Reclaim the ruins of a once-great city.',
    available: true,
    players: '1–6',
  },
  {
    id: 'curse-of-azure-bonds',
    title: 'Curse of the Azure Bonds',
    year: '1989',
    setting: 'Tantras, Forgotten Realms',
    tagline: 'Mysterious bonds compel you to serve dark masters.',
    available: false,
    players: '1–6',
  },
  {
    id: 'secret-silver-blades',
    title: 'Secret of the Silver Blades',
    year: '1990',
    setting: 'Vaasa, Forgotten Realms',
    tagline: 'Descend into frozen depths to face an ancient evil.',
    available: false,
    players: '1–6',
  },
  {
    id: 'champions-of-krynn',
    title: 'Champions of Krynn',
    year: '1990',
    setting: 'Ansalon, Dragonlance',
    tagline: 'The War of the Lance has ended — or has it?',
    available: false,
    players: '1–6',
  },
  {
    id: 'pools-of-darkness',
    title: 'Pools of Darkness',
    year: '1991',
    setting: 'Moonsea, Forgotten Realms',
    tagline: 'Face the god Bane himself in the final reckoning.',
    available: false,
    players: '1–6',
  },
  {
    id: 'gateway-savage-frontier',
    title: 'Gateway to the Savage Frontier',
    year: '1991',
    setting: 'The North, Forgotten Realms',
    tagline: 'War threatens the untamed wilds of the North.',
    available: false,
    players: '1–6',
  },
];

export default function GameLibrary() {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleSignOut = async () => {
    try {
      await logout();
    } finally {
      navigate('/', { replace: true });
    }
  };

  return (
    <div className="gl-root">
      {/* Header */}
      <header className="gl-header">
        <div className="gl-header-inner">
          <div className="gl-brand" onClick={() => navigate('/')} role="button" tabIndex={0}>
            <svg className="gl-logo-svg" viewBox="0 0 80 80" fill="none">
              <rect x="18" y="54" width="44" height="10" rx="2" fill="#c9a84c" opacity="0.9"/>
              <rect x="24" y="48" width="32" height="8" rx="1" fill="#c9a84c" opacity="0.9"/>
              <path d="M24 48 L10 44 L14 48 Z" fill="#c9a84c" opacity="0.7"/>
              <rect x="20" y="36" width="40" height="14" rx="2" fill="#c9a84c"/>
              <rect x="46" y="10" width="5" height="30" rx="2" fill="#8b6914" transform="rotate(-30 46 10)"/>
              <rect x="28" y="6" width="22" height="14" rx="3" fill="#c9a84c" transform="rotate(-30 28 6)"/>
              <circle cx="42" cy="34" r="2" fill="#fff" opacity="0.9"/>
            </svg>
            <span className="gl-brand-name">
              <span className="gl-forge">Forge</span><span className="gl-mark">mark</span>
            </span>
          </div>
          <nav className="gl-nav">
            <button className="gl-nav-btn" onClick={handleSignOut}>Sign Out</button>
          </nav>
        </div>
      </header>

      {/* Page title */}
      <div className="gl-hero">
        <div className="gl-ornament-line" aria-hidden="true">
          <span className="gl-ornament-dash"></span>
          <span className="gl-ornament-gem">◆</span>
          <span className="gl-ornament-dash"></span>
        </div>
        <h1 className="gl-title">The Game Library</h1>
        <p className="gl-subtitle">Choose your adventure from the chronicles of the Realms</p>
        <div className="gl-ornament-line" aria-hidden="true">
          <span className="gl-ornament-dash"></span>
          <span className="gl-ornament-gem">◆</span>
          <span className="gl-ornament-dash"></span>
        </div>
      </div>

      {/* Game grid */}
      <main className="gl-grid-wrap">
        <div className="gl-grid">
          {GAMES.map((game, i) => (
            <div
              key={game.id}
              className={`gl-card ${game.available ? 'gl-card-available' : 'gl-card-locked'}`}
              style={{ animationDelay: `${i * 0.08}s` }}
              onClick={() => game.available && navigate(`/games/${game.id}`)}
              role={game.available ? 'button' : undefined}
              tabIndex={game.available ? 0 : undefined}
              onKeyDown={e => game.available && e.key === 'Enter' && navigate(`/games/${game.id}`)}
              aria-label={game.available ? `Play ${game.title}` : `${game.title} — coming soon`}
            >
              {/* Corner accents */}
              <div className="gl-corner gl-corner-tl" aria-hidden="true"/>
              <div className="gl-corner gl-corner-tr" aria-hidden="true"/>
              <div className="gl-corner gl-corner-bl" aria-hidden="true"/>
              <div className="gl-corner gl-corner-br" aria-hidden="true"/>

              {/* Badge */}
              {game.available
                ? <span className="gl-badge gl-badge-available">Available</span>
                : <span className="gl-badge gl-badge-soon">Coming Soon</span>
              }

              <div className="gl-card-inner">
                <div className="gl-year">{game.year}</div>
                <h2 className="gl-game-title">{game.title}</h2>
                <p className="gl-setting">{game.setting}</p>
                <p className="gl-tagline">"{game.tagline}"</p>

                <div className="gl-meta">
                  <span className="gl-meta-item">⚔ {game.players} Players</span>
                  <span className="gl-meta-item">5.5e Rules</span>
                </div>

                {game.available && (
                  <button className="gl-play-btn" tabIndex={-1} aria-hidden="true">
                    Enter Game
                  </button>
                )}

                {!game.available && (
                  <div className="gl-lock-overlay" aria-hidden="true">
                    <span className="gl-lock-icon">🔒</span>
                    <span className="gl-lock-text">In Development</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>

      <footer className="gl-footer">
        <p>© 2026 Forgemark · Bringing classic adventures into the modern age</p>
      </footer>
    </div>
  );
}
